// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import updateNewsListParameter from "@conklin/App/Handlers/NewsList/UpdateNewsListParameter";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import Checkbox from "@conklin/MobiusOverrides/Checkbox/Checkbox";
import Link from "@conklin/MobiusOverrides/Link/Link";
import { XNGAGE_CUSTOM_WIDGET } from "@conklin/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    showMoreExpanded = "showMoreExpanded",
    accordionExpanded = "accordionExpanded",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showMoreExpanded]: boolean;
        [fields.accordionExpanded]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        categories: state.xNewsList.categories,
        location: getLocation(state),
        selectedCategories: state.xNewsList.parameter?.categories || "",
    };
};

const mapDispatchToProps = {
    updateNewsListParameter,
};

interface NewsListFilterStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    accordion?: AccordionSectionPresentationProps;
    showMoreLink?: LinkPresentationProps;
    showLessLink?: LinkPresentationProps;
    accordionWrapper?: InjectableCss;
    filtersWrapper?: InjectableCss;
    checkbox?: CheckboxPresentationProps;
    categoryCount?: TypographyPresentationProps;
}

export const newsListFilterStyles: NewsListFilterStyles = {};

const styles = newsListFilterStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const NewsListFilter: FC<Props> = ({ fields, categories, location, updateNewsListParameter, selectedCategories }) => {
    const history = useHistory();
    const allCategories = translate("All Categories");
    const [newsListsFilters, setNewsListsFilters] = React.useState({} as { [key: string]: boolean });
    const [expanded, setExpanded] = React.useState(fields.accordionExpanded);
    const [showMore, setShowMore] = React.useState(fields.showMoreExpanded);

    React.useEffect(() => {
        setNewsListsFilters(
            selectedCategories
                ? selectedCategories.split(",").reduce((acc: Record<string, boolean>, curr: string) => {
                      acc[curr] = true;
                      return acc;
                  }, {})
                : {},
        );
    }, [selectedCategories]);

    const filterByCategory = (category: string) => {
        let updatedFilters: any;
        if (category === allCategories) {
            updatedFilters = {};
        } else {
            updatedFilters = { ...newsListsFilters, [category]: !newsListsFilters[category] };
            const allFalse = Object.values(updatedFilters).every(value => value === false);

            if (allFalse) {
                updatedFilters = {};
            }
        }
        const categoriesQuery = Object.keys(updatedFilters)
            .filter(key => updatedFilters[key])
            .join(",");

        updateNewsListParameter({
            parameter: {
                page: 1,
                pageSize: 12,
                categories: categoriesQuery,
            },
            onComplete: () => {
                history.push(
                    `${location.pathname}?page=${1}&pageSize=${12}${
                        categoriesQuery !== "" ? `&categories=${categoriesQuery}` : ``
                    }`,
                );
            },
        });
    };

    const limitedCategories = showMore ? categories : categories.slice(0, 4);
    const allCategoriesCount = categories.reduce((sum, item) => sum + item.count, 0);
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title}>{translate("Refine Results")}</Typography>
            <StyledWrapper {...styles.accordionWrapper}>
                <AccordionSection
                    title={translate("Blog Categories")}
                    expanded={expanded}
                    onTogglePanel={() => setExpanded(!expanded)}
                    {...styles.accordion}
                >
                    <StyledWrapper {...styles.filtersWrapper}>
                        <Checkbox
                            onChange={() => filterByCategory(allCategories)}
                            checked={Object.keys(newsListsFilters).length === 0}
                            {...styles.checkbox}
                        >
                            {allCategories}
                            <Typography {...styles.categoryCount}>({allCategoriesCount})</Typography>
                        </Checkbox>
                        {limitedCategories.map((category, index) => {
                            return (
                                <Checkbox
                                    key={`${category.categoryName}_${index}`}
                                    onChange={() => filterByCategory(category.categoryName)}
                                    checked={newsListsFilters[category.categoryName]}
                                    {...styles.checkbox}
                                >
                                    {category.categoryName}
                                    <Typography {...styles.categoryCount}>({category.count})</Typography>
                                </Checkbox>
                            );
                        })}
                        {categories.length > 4 &&
                            (showMore ? (
                                <Link onClick={() => setShowMore(false)} {...styles.showLessLink}>
                                    {translate("Show Less")}
                                </Link>
                            ) : (
                                <Link onClick={() => setShowMore(true)} {...styles.showMoreLink}>
                                    {translate("Show More")}
                                </Link>
                            ))}
                    </StyledWrapper>
                </AccordionSection>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const NewsListFilterWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NewsListFilter),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                name: fields.accordionExpanded,
                displayName: "Accordion Expanded by Default",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showMoreExpanded,
                displayName: "Show More Expanded by Default",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default NewsListFilterWidget;
